import debounce from "awesome-debounce-promise";
import { type FC, useState } from "react";
import { useDispatch } from "react-redux";
import type { BaseFieldProps } from "redux-form/lib/Field";

import { type GenomeSearch, type Organism } from "../../graphql.ts";
import { searchSequencedGenomes } from "../../redux/modules/genome-browser.ts";
import { type ReduxDispatch } from "../../redux/redux-utils.ts";
import ReactSelectField, { ReactSelectReduxFormField } from "../core/redux-form/ReactSelectField.tsx";

interface ISequencedGenomeSelector {
  name?: string;
  fieldName: string;
  submitting: boolean;
  clearable?: boolean;
  multiple?: boolean;
  validate?: BaseFieldProps["validate"];
}

const SequencedGenomeSelector: FC<ISequencedGenomeSelector> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  function loadOptions(searchTerm: string) {
    setIsLoading(true);
    return dispatch(searchSequencedGenomes(searchTerm)).then((genomeSearch: GenomeSearch) => {
      setIsLoading(false);
      return genomeSearch.genomes;
    });
  }

  const debouncedLoadOptions = debounce(loadOptions, 300, { onlyResolvesLast: true });

  return (
    <ReactSelectReduxFormField
      key={props.fieldName}
      name={props.fieldName}
      label={props.name || props.fieldName}
      horizontalLayout={false}
      placeholder="Select or type to search..."
      component={ReactSelectField}
      isClearable={props.clearable}
      isSearchable={true}
      async={true}
      isMulti={props.multiple}
      loadOptions={debouncedLoadOptions}
      getOptionLabel={(option: Organism) => option.name}
      getOptionValue={(option: Organism) => option.id}
      isDisabled={props.submitting}
      isLoading={isLoading || props.submitting}
      noOptionsMessage={() => "No matching genome..."}
      validate={props.validate}
    />
  );
};

export { SequencedGenomeSelector };
